import { ErrorKeys } from 'consts/errorKeys'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { giftAccessThunks } from 'store/features/aiAvatar/thunks/giftAccess'
import slice from 'store/features/aiAvatar/slice'
import { TAppDispatch, TGetState } from 'store/types'
import { handleApiError } from 'store/utils'
import { generationProgressService } from 'services/generationProgress'
import { IAIAvatarSocketMessage } from './types'

/**
 * Completion handler: the avatar generation produced an array of video samples
 * (already full media items). Find the temporary `aiAvatar` placeholder and
 * replace it with those samples as permanent `aiAvatar` clips, laid out
 * consecutively on the placeholder's line — in the present timeline AND in every
 * undo/redo snapshot that still holds the placeholder, so the finished clips
 * survive undo/redo (e.g. generate -> undo -> generation finishes -> redo).
 */
const processCompletedAvatar =
  (socketData: IAIAvatarSocketMessage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const hash = socketData.ai_avatar_video_hash
    const projectId = String(socketData.ai_avatar_project_id)

    // Search present + past + future (`pendingAiAvatarMediaItems` folds in
    // `pendingMediasFromPastAndFuture`) so a completion that lands while the user
    // has undone past the placeholder still resolves to the right item instead
    // of being dropped. It may be gone entirely if the user deleted it before
    // the event arrived — then there is nothing to replace.
    //
    // Correlate the placeholder: fresh-gen carries the hash as the item `id`;
    // regen carries the generation's projectId as `assetId` (the clip's `id`
    // can't change). The `assetId === hash` branch is kept for legacy in-flight
    // regen clips created before regen switched to projectId-keying.
    const placeholder = projectDataSelectors
      .pendingAiAvatarMediaItems(state)
      .find(
        item =>
          item.id === hash ||
          item.assetId === hash ||
          item.assetId === projectId
      )
    if (!placeholder) return

    const samples = socketData.mediaItems
    if (!samples?.length) return

    // Splice the placeholder -> samples across present/past/future. This reducer
    // mutates the history stacks directly and never pushes to the undo stack, so
    // no `isUndoable` flag is needed (matches how aiVideo finalizes generation).
    dispatch(
      projectDataThunks.replacePendingPlaceholderWithSamples({
        id: placeholder.id,
        samples
      })
    )

    // Regeneration saves the original clip as a processing item (so a failure
    // can restore it). On success the clip has been replaced, so drop that
    // snapshot. No-op for fresh generations, which never save one.
    dispatch(projectDataThunks.removeFromProcessingItems(placeholder.id))

    // Snap the loader to 100% and tear down the generator + persisted
    // progress. The placeholder is already gone from the timeline, so this is
    // primarily cleanup (no zombie generator lingering in localStorage).
    generationProgressService.complete({
      mediaItemId: placeholder.id,
      assetId: placeholder.assetId
    })

    // A free user just consumed their free trial — re-check eligibility so the
    // generate footer flips to the upgrade state (self-guards to free users).
    // Also clear the mid-render awaiting flag: this is the fallback that releases
    // the footer loading state if the earlier `voiceover_generated` was dropped.
    dispatch(giftAccessThunks.checkGiftAccess())
    dispatch(slice.actions.setAwaitingGiftRecheck(false))
  }

/**
 * Failure handler: generation failed. Find the `aiAvatar` item we were
 * generating into and hand it to `handleFailedGenerationForMediaItem`, which
 * restores the clip's pre-generation data if a processing-item snapshot exists
 * (regeneration) or removes it otherwise (fresh generation), and tears down the
 * loader. If the item is already gone, just clean up the persisted progress.
 */
const processFailedAvatar =
  (socketData: IAIAvatarSocketMessage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const hash = socketData.ai_avatar_video_hash
    const projectId = String(socketData.ai_avatar_project_id)

    // Search present + past + future (mirrors `processCompletedAvatar`) so a
    // failure that lands while the user has undone past the placeholder still
    // resolves to the right clip — and restores its pre-generation data —
    // instead of being dropped, which would leave a regen snapshot dangling
    // and the clip stuck PENDING on redo.
    const placeholder = projectDataSelectors
      .pendingAiAvatarMediaItems(state)
      .find(
        item =>
          item.id === hash ||
          item.assetId === hash ||
          item.assetId === projectId
      )

    if (placeholder) {
      dispatch(
        projectDataThunks.handleFailedGenerationForMediaItem(placeholder.id)
      )

      // Surface the failure to the user (mirrors `processFailedAIVideo`): a
      // backend-supplied `message` is shown verbatim, otherwise the generic
      // avatar-generation-failed copy.
      dispatch(
        handleApiError(
          socketData.message
            ? ErrorKeys.CUSTOM_API_ERROR
            : ErrorKeys.AI_AVATAR_GENERATION_FAILED,
          socketData.message
        )
      )
    } else {
      // Item already deleted before the event arrived — just tear down the
      // loader and its persisted progress so it can't resume on reload.
      generationProgressService.stop({
        mediaItemId: hash,
        assetId: projectId,
        shouldRemoveFromStorage: true
      })
    }

    // Re-check eligibility and release the mid-render awaiting flag (fallback in
    // case `voiceover_generated` never arrived) — mirrors processCompletedAvatar.
    dispatch(giftAccessThunks.checkGiftAccess())
    dispatch(slice.actions.setAwaitingGiftRecheck(false))
  }

/**
 * Mid-render milestone: the voiceover (TTS) has been generated, which is when a
 * free user's gift/credit is actually consumed — well before the talking-head
 * video FINISHES (~90s). Re-check gift access now so the generate footer flips
 * to the correct state (free-trial again if credits remain, or upgrade) as soon
 * as the truth is known, then release the footer loading state. `checkGiftAccess`
 * self-guards to free users, and `isAwaitingGiftRecheck` is only ever set for
 * free users, so this is a no-op for paid users. Gift access is per-user, so no
 * clip correlation is needed.
 */
const processVoiceoverGenerated = () => async (dispatch: TAppDispatch) => {
  await dispatch(giftAccessThunks.checkGiftAccess())
  dispatch(slice.actions.setAwaitingGiftRecheck(false))
}

export const aiAvatarSocketHandlers = {
  processCompletedAvatar,
  processFailedAvatar,
  processVoiceoverGenerated
}
